import { Controller } from "@hotwired/stimulus";

/**
 * Bidirectionally syncs a range slider and a number input so devs can
 * either drag or type. Both children carry the actual playground targets
 * (panelParam / advancedExtraParam) so their change events still flow
 * through the existing generation pipeline; this controller only mirrors
 * the value across the pair.
 */
export default class RangeNumberPairController extends Controller {
  static targets = ["range", "number"];

  declare rangeTarget: HTMLInputElement;
  declare numberTarget: HTMLInputElement;

  // Set while syncFromNumber's "cleared" branch dispatches a synthetic input
  // event on the slider — used by syncFromRange to ignore that re-entry so
  // it doesn't undo the clear by copying the slider's residual numeric
  // value back into the number input.
  private suppressSync = false;

  syncFromRange(): void {
    if (!this.numberTarget) return;
    if (this.suppressSync) return;
    this.numberTarget.value = this.rangeTarget.value;
    // The user just picked a value — drop the "auto" flag so downstream
    // collectors (advancedExtraParam, panelParam) start including it.
    this.markEngaged();
  }

  syncFromNumber(): void {
    if (!this.rangeTarget) return;
    if (this.numberTarget.value === "") {
      // Cleared back to "auto" — restore the unset state so the playground
      // controller skips this param again on the next collection pass.
      // Snap the slider thumb back to the midpoint of [min..max] so the
      // visual matches the initial auto state. Range inputs reject empty
      // strings, so we mirror the browser's own fallback explicitly.
      this.rangeTarget.dataset.auto = "true";
      this.rangeTarget.classList.add("opacity-50");
      const min = Number(this.rangeTarget.min);
      const max = Number(this.rangeTarget.max);
      if (!Number.isNaN(min) && !Number.isNaN(max)) {
        this.rangeTarget.value = String((min + max) / 2);
      }
      this.suppressSync = true;
      try {
        this.rangeTarget.dispatchEvent(new Event("input", { bubbles: true }));
      } finally {
        this.suppressSync = false;
      }
      return;
    }
    const min = Number(this.rangeTarget.min || -Infinity);
    const max = Number(this.rangeTarget.max || Infinity);
    const raw = Number(this.numberTarget.value);
    if (Number.isNaN(raw)) return;
    const clamped = Math.min(Math.max(raw, min), max);
    this.rangeTarget.value = String(clamped);
    this.markEngaged();
    // Re-dispatch so the canonical change handler (which targets the slider)
    // sees the new value when the user typed instead of dragged.
    this.rangeTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  private markEngaged(): void {
    delete this.rangeTarget.dataset.auto;
    this.rangeTarget.classList.remove("opacity-50");
  }
}
